import { useEffect, useState } from "react";
import MainPanel from "../../components/MainPanel/MainPanel";
import DefaultLayout from "../../layout/Layout";
import AccountHeader from "./Header";
import "./account.scss";
import { DataGrid } from "@mui/x-data-grid";
import { MoreVert, Warning } from "@mui/icons-material";
import { Divider, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setLoading, updateTags } from "../../redux/actions";
import { Icon } from "@iconify/react";
import { editAccount, getTags, sweep } from "../../service";
import { useCustomSnackbar } from "../../components/Misc/useSnackbar";
import Modal from "../../components/Misc/Modal";
import Input from "../../components/UIElements/Input";

const Account = () => {
  const [copied, setCopied] = useState(false);
  const columns = [
    {
      field: "id",
      headerName: "AccountID",
      sortable: false,
      width: 100,
      minWidth: 100,
    },
    {
      field: "username",
      headerName: "Username",
      sortable: false,
      width: 300,
      minWidth: 250,
      renderCell: (param) => {
        const { bad_cred_type, bad_creds } = param.row;
        if (bad_creds) {
          return (
            <span className="text-danger flex items-center">
              {param.value}{" "}
              <Tooltip title={bad_cred_type}>
                <Warning className="ml-2 cursor-pointer" />
              </Tooltip>{" "}
            </span>
          );
        } else {
          return param.value;
        }
      },
    },
    {
      field: "password",
      headerName: "Password",
      sortable: false,
      width: 250,
      renderCell: (e) => {
        const { value, id } = e;
        return (
          <div className="flex items-center" key={id}>
            <span className="mr-auto">{value}</span>{" "}
            {copied !== id ? (
              <Icon
                icon="mdi:content-copy"
                id={id}
                onClick={(event) => copyPwd({ value, id, event })}
                className={`cursor-pointer mr-2 w-[18px] h-[18px]`}
              />
            ) : (
              <Icon icon="codicon:check-all" className="text-success" />
            )}
          </div>
        );
      },
    },
    {
      field: "site",
      headerName: "Site",
      sortable: false,
      width: 120,
      renderCell: (e) => {
        const { row } = e;
        const { site } = row.site;
        let siteValue = site ? (
          site.includes("ticketmaster") ? (
            <a href={site} target="_blank" rel="noopener noreferrer">
              Ticketmaster
            </a>
          ) : (
            site
          )
        ) : (
          "Unknown"
        );
        return siteValue;
      },
    },
    {
      field: "active",
      headerName: "Active",
      sortable: false,
      width: 70,
      renderCell: (param) => {
        const { active } = param.row;
        return active ? "Active" : "Inactive";
      },
    },
    {
      field: "internal_tags",
      headerName: "Tags",
      sortable: false,
      width: 180,
      renderCell: (param) => {
        const { internal_tags } = param.row;
        return internal_tags ? internal_tags.join(", ") : "";
      },
    },
    {
      field: "last_crawled",
      headerName: "Last Crawled",
      sortable: false,
      width: 180,
      renderCell: (param) => {
        const { value } = param;
        return value ? new Date(value).toLocaleString() : "";
      },
    },
    {
      field: "internal_notes",
      headerName: "Notes",
      sortable: false,
      minWidth: 250,
      flex: 1,
    },
    {
      field: "option",
      headerName: "",
      width: 70,
      sortable: false,
      renderCell: (param) => {
        return (
          <IconButton
            onClick={(e) => handleClick(e, param)}
            aria-controls="optionMenu"
            aria-haspopup="true"
          >
            <MoreVert className="text-white" />
          </IconButton>
        );
      },
      disableColumnMenu: true,
      disableColumnResize: true,
    },
  ];

  const toast = useCustomSnackbar();
  const [accounts, setAccounts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [accountId, setAccountId] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const { user } = useSelector((state) => state.user);
  const { tags } = useSelector((state) => state.account);
  const [showModal, setShowModal] = useState(false);
  const [password, setpassword] = useState("");
  const [oldPwd, setOldPwd] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (user.role === "admin" && tags == null) {
      getTagsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, user.role]);

  const [totalRows, setTotalRows] = useState(0);

  const handleClick = (event, param) => {
    setAnchorEl(event.currentTarget);
    setAccountId(param.id);
    setOldPwd(param.row.password);
  };

  const copyPwd = ({ value, id, event }) => {
    if (event.target.id === `${id}`) {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopied(id);
          toast({
            message: 'Password copied to clipboard',
            type: 'success',
          });
        })
        .catch((err) => console.error("Could not copy text: ", err));
      setTimeout(() => setCopied(null), 1500);
    }
  };

  const changePwd = () => {
    setAnchorEl(null);
    setShowModal(true);
  }

  const handleChangePassword = async () => {
    let accountData = accounts.find((acc) => acc.id === accountId);
    accountData.password = password;
    setShowModal(false);
    dispatch(setLoading({ stat: true, text: "Changing password" }));
    const { status, account } = await editAccount({
      id: accountId,
      formData: accountData
    });
    dispatch(setLoading(false));
    if (status) {
      toast({
        type: "success",
        message: "Password changed successfully",
      });
      setAccounts((prev) => {
        const updatedAccounts = [...prev];
        const index = updatedAccounts.findIndex((acc) => acc.id === accountId);
        if (index !== -1) {
          updatedAccounts[index] = account;
        }
        return updatedAccounts;
      })
    }
  }

  const sweepAccount = async (event) => {
    setAnchorEl(null);
    dispatch(setLoading({ stat: true, text: "Sweeping Account" }));
    const { status, message } = await sweep({ event, accountId });
    const option = {
      type: status ? "success" : "danger",
      message,
    };
    dispatch(setLoading(false));
    toast(option);
  };
  const getTagsData = async () => {
    dispatch(setLoading({ stat: true, text: "Getting Tags" }));
    const { data } = await getTags();
    dispatch(setLoading(false));
    dispatch(updateTags(data));
  };

  const accountsData = useSelector((state) => state.account);
  useEffect(() => {
    if (accountsData) {
      const { count, accounts } = accountsData.accounts;
      setTotalRows(count || 0);
      setAccounts(accounts);
    }
  }, [accountsData]);

  const onChange = (e) => {
    setPaginationModel(e);
  };
  return (
    <DefaultLayout isLogin={false}>
      <MainPanel>
        <div className="flex flex-col h-full">
          <AccountHeader paginationModel={paginationModel} />
          <div
            style={{ height: "calc(100vh - 156px)", width: "100%" }}
            className="mt-4"
          >
            <DataGrid
              columns={columns}
              rows={accounts}
              columnBufferPx={100}
              rowBufferPx={100}
              classes={{
                root: "bg-boxdark border-boxdark-2",
              }}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={onChange}
              pagination
              rowCount={totalRows}
              sx={{
                color: "white",
                "& .*-MuiDataGrid-columnHeaderRow": {
                  background: "red",
                },
              }}
              checkboxSelection
              disableRowSelectionOnClick={true}
              disableColumnSelector={true}
              slotProps={{
                toolbar: { csvOptions: { fields: ["username", "password", "site"] } },
              }}
            />
          </div>
          <Menu
            id="optionMenu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem>
              <Link
                to={`/account/detail?id=${accountId}`}
                className="inline-flex items-center justify-center text-center font-medium text-grey hover:bg-opacity-90"
              >
                View Detail
              </Link>
            </MenuItem>
            <MenuItem onClick={() => changePwd()}>Change Password</MenuItem>
            <Divider />
            <MenuItem onClick={() => sweepAccount("sweep")}>Sweep</MenuItem>
            <MenuItem onClick={() => sweepAccount("full_sweep")}>
              Full Sweep
            </MenuItem>
          </Menu>
        </div>
        <Modal show={showModal}>
          <div className="md:px-17.5 w-full max-w-230 rounded-lg bg-white px-8 py-12 dark:bg-boxdark md:py-15">
            <div className="mb-4.5 flex flex-col gap-6 xl:flex-row relative">
              <Input
                label="Old Password"
                name="old_password"
                value={oldPwd}
                id={oldPwd}
                disabled
              />
              <div className="absolute top-[50px] right-0">
                <Icon
                  icon="mdi:content-copy"
                  id={oldPwd}
                  onClick={(event) => copyPwd({ value: oldPwd, id: oldPwd, event })}
                  className={`cursor-pointer mr-2 w-[24px] h-[24px]`}
                />
              </div>
            </div>
            <div className="mb-4.5 flex flex-col gap-6 xl:flex-row">
              <Input
                label="New Password"
                name="password"
                value={password}
                onChange={(e) => setpassword(e.target.value)}
              />
            </div>
            <div>
              <div className="-mx-3 flex flex-wrap gap-y-4">
                <div className="2xsm:w-1/2 w-full px-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="block w-full rounded border border-stroke bg-gray p-3 text-center font-medium text-black transition hover:border-meta-1 hover:bg-meta-1 hover:text-white dark:border-strokedark dark:bg-meta-4 dark:text-white dark:hover:border-meta-1 dark:hover:bg-meta-1"
                  >
                    Cancel
                  </button>
                </div>
                <div className="2xsm:w-1/2 w-full px-3">
                  <button
                    onClick={handleChangePassword}
                    className="block w-full rounded border border-primary bg-primary p-3 text-center font-medium text-white transition hover:bg-opacity-90"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </MainPanel>
    </DefaultLayout>
  );
};

export default Account;
